import * as R from 'ramda'
import { createSelector } from 'reselect'
import { memoize } from 'utils/function'

const UNSAVED_KEY = 'add'

// SLICE SELECTORS
// getModelSlice :: str -> state -> Model
export const getModelSlice = model => R.path(['orm', model])

export const getOrm = R.prop('orm')

// ids of all model entries in store
// getItemIds :: transformer -> sorter -> [{k:v}] -> [k]
const getItemIds = (transformer, sorter) =>
  R.pipe(
    R.into(
      [],
      R.compose(
        R.filter(R.identity),
        R.reject(R.propEq('__status', 'deleted')),
        transformer,
      ),
    ),
    sorter,
    R.pluck('id'),
    R.map(R.when(R.is(Number), R.toString)),
  )

// get react-select options for model
const getSelectOptions = R.pipe(
  R.values,
  R.reject(R.propEq('__status', 'deleted')),
  R.filter(R.identity),
  R.map(({ id, label, name, title }) => ({
    value: id,
    label: label || name || title,
  })),
)

// ids of entries selected in the datatable
// getSelectedItemIds :: Selected -> [pk]
const getSelectedItemIds = R.pipe(R.filter(R.identity), R.keys)

// mergeDirtyItem :: {props, __dirty:{props}} -> {props, isDirty:bool}
// Combine pristine and dirty props from model entry in store
const mergeDirtyItem = ({ __dirty: dirty = {}, ...props }) =>
  R.isEmpty(dirty)
    ? { ...props, isDirty: false }
    : { ...props, ...dirty, isDirty: true }

const isOk = R.chain(
  R.assoc('ok'),
  R.ifElse(
    R.propEq('pk', 'add'),
    R.T,
    R.pipe(
      R.prop('__status'),
      R.includes(R.__, ['', 'fetching', 'deleted', 'error']),
      R.not,
    ),
  ),
)

// modelSelectors :: str -> {str: selector}
export const modelSelectors = memoize(sliceName => {
  const getModel = createSelector(getModelSlice(sliceName), R.identity)
  const selected = createSelector(getModel, R.pathOr({}, ['meta', 'selected']))
  const items = createSelector(
    R.pipe(getModel, R.propOr({}, 'items')),
    R.omit([UNSAVED_KEY]),
  )
  return {
    getModel,
    getLoaded: createSelector(getModel, R.has('items')),
    getItems: items,
    getItemIds: (transformer: any = R.identity, sorter: any = R.identity) =>
      createSelector(items, R.pipe(R.values, getItemIds(transformer, sorter))),
    getOptions: createSelector(items, getSelectOptions),
    getSelected: selected,
    getSelectedItemIds: createSelector(selected, getSelectedItemIds),
    getItemSelected: (state, { pk }) =>
      R.pipe(selected, R.propOr(false, pk))(state),
    getDirty: createSelector(
      (state, { pk }) => R.pipe(getModel, R.pathOr({}, ['items', pk]))(state),
      R.prop('__dirty'),
    ),
    getItem: (id?: any) =>
      createSelector(
        (state: any, { pk = id }: any = {}) =>
          R.pipe(getModel, R.pathOr({}, ['items', pk]))(state),
        R.pipe(
          mergeDirtyItem,
          isOk,
          R.when(R.propEq('id', UNSAVED_KEY), R.assoc('unsaved', true)),
        ),
      ),
    getItemValue: (id?: any, key?: any) =>
      createSelector(
        (state: any, { pk = id, name = key }: any = {}) =>
          R.pipe(
            getModel,
            R.pathOr({}, ['items', pk]),
            R.ifElse(
              R.hasPath(['__dirty', name]),
              R.path(['__dirty', name]),
              R.path([name]),
            ),
          )(state),
        R.objOf('value'),
      ),
  }
})
